"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

interface PartsDisplayItem {
	src: string;
}

interface PartsDisplayConfig {
	namespace: string;
	titleKey: string;
	subtitleKey: string;
	altKey: string;
	items: PartsDisplayItem[];
	imageBgColor?: string;
}

const METAL_CASTING_IMG_BASE = "/image/new-version/casting/Metal-Casting/Parts-Display";
const SAND_CASTING_IMG_BASE = "/image/new-version/casting/Sand-Casting";
const DIE_CASTING_IMG_BASE = "/image/new-version/casting/Die-Casting";
const SPRING_MANUFACTURING_IMG_BASE = "/image/new-version/more/SpringManufacturing/Parts Display";
const PARTS_DISPLAY_CONFIG: Record<string, PartsDisplayConfig> = {
	"metal-casting": {
		namespace: "new-metal-casting",
		titleKey: "partsDisplayTitle",
		subtitleKey: "partsDisplaySubtitle",
		altKey: "partsDisplayImageAlt",
		items: [
			{ src: `${METAL_CASTING_IMG_BASE}/metal-casting-1.webp` },
			{ src: `${METAL_CASTING_IMG_BASE}/metal-casting-2.webp` },
			{ src: `${METAL_CASTING_IMG_BASE}/metal-casting-3.webp` },
			{ src: `${METAL_CASTING_IMG_BASE}/metal-casting-4.webp` },
			{ src: `${METAL_CASTING_IMG_BASE}/metal-casting-5.webp` },
			{ src: `${METAL_CASTING_IMG_BASE}/metal-casting-6.webp` },
			{ src: `${METAL_CASTING_IMG_BASE}/metal-casting-7.webp` },
		],
		imageBgColor: "#bfbfbf",
	},
	"sand-casting": {
		namespace: "new-sand-casting",
		titleKey: "partsDisplayTitle",
		subtitleKey: "partsDisplaySubtitle",
		altKey: "partsDisplayImageAlt",
		items: [
			{ src: `${SAND_CASTING_IMG_BASE}/banner/sand-casting-1.webp` },
			{ src: `${SAND_CASTING_IMG_BASE}/banner/sand-casting-2.webp` },
			{ src: `${SAND_CASTING_IMG_BASE}/banner/sand-casting-3.webp` },
			{ src: `${SAND_CASTING_IMG_BASE}/Success-Stories/automotive-industry.webp` },
			{ src: `${SAND_CASTING_IMG_BASE}/Success-Stories/energy-sector.webp` },
			{ src: `${SAND_CASTING_IMG_BASE}/Why-Choose/professional-sand-casting-solutions.webp` },
			{ src: `${SAND_CASTING_IMG_BASE}/post-casting-cnc-machining.webp` },
		],
		imageBgColor: "#bfbfbf",
	},
	"die-casting": {
		namespace: "new-die-casting",
		titleKey: "partsDisplayTitle",
		subtitleKey: "partsDisplaySubtitle",
		altKey: "partsDisplayImageAlt",
		items: [
			{ src: `${DIE_CASTING_IMG_BASE}/Die-Casting-Parts-Display/die-casting-parts-1.png` },
			{ src: `${DIE_CASTING_IMG_BASE}/Die-Casting-Parts-Display/die-casting-parts-2.png` },
			{ src: `${DIE_CASTING_IMG_BASE}/Die-Casting-Parts-Display/die-casting-parts-3.png` },
			{ src: `${DIE_CASTING_IMG_BASE}/Die-Casting-Parts-Display/die-casting-parts--4.png` },
			{ src: `${DIE_CASTING_IMG_BASE}/Die-Casting-Parts-Display/die-casting-parts-5.png` },
			{ src: `${DIE_CASTING_IMG_BASE}/Die-Casting-Parts-Display/die-casting-parts-6.png` },
		],
		imageBgColor: "#bfbfbf",
	},
	"spring-manufacturing": {
		namespace: "new-spring-manufacturing",
		titleKey: "partsDisplayTitle",
		subtitleKey: "partsDisplaySubtitle",
		altKey: "partsDisplayImageAlt",
		items: [
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-1.webp` },
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-2.webp` },
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-3.webp` },
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-4.webp` },
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-5.webp` },
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-6.webp` },
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-7.webp` },
			{ src: `${SPRING_MANUFACTURING_IMG_BASE}/spring-manufacturing-parts-8.webp` },
		],
		imageBgColor: "#bfbfbf",
	},
};

export default function PartsDisplay({ slug, className }: { slug: string; className?: string }) {
	const config = PARTS_DISPLAY_CONFIG[slug];
	const t = useTranslations(config?.namespace);

	const containerRef = useRef<HTMLDivElement>(null);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isMobile, setIsMobile] = useState(false);
	const [containerWidth, setContainerWidth] = useState(0);

	useEffect(() => {
		const update = () => {
			setIsMobile(window.innerWidth < 768);
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};
		update();
		window.addEventListener("resize", update);
		return () => window.removeEventListener("resize", update);
	}, []);

	if (!config) return null;

	const { items, titleKey, subtitleKey, altKey, imageBgColor = "#bfbfbf" } = config;

	const visibleCount = isMobile ? 2 : 4;
	const gap = 16;
	const itemWidth = containerWidth > 0 ? (containerWidth - (visibleCount - 1) * gap) / visibleCount : 0;
	const maxIndex = Math.max(0, items.length - visibleCount);
	const slideCount = maxIndex + 1;

	useEffect(() => {
		if (currentIndex > maxIndex) setCurrentIndex(0);
	}, [maxIndex, currentIndex]);

	const nextSlide = useCallback(() => {
		setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
	}, [maxIndex]);

	useEffect(() => {
		if (slideCount <= 1) return;
		const timer = setInterval(nextSlide, 4000);
		return () => clearInterval(timer);
	}, [nextSlide, slideCount]);

	return (
		<section className={cn("bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">{t(titleKey)}</h2>
				<p className="mb-6 mt-2 text-sm text-gray-900 sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{t(subtitleKey)}
				</p>

				<div ref={containerRef} className="relative overflow-hidden">
					<div
						className="flex gap-4 transition-transform duration-500 ease-in-out"
						style={{
							transform: `translateX(-${currentIndex * (itemWidth + gap)}px)`,
						}}
					>
						{items.map((item, index) => (
							<div
								key={index}
								className="flex-shrink-0"
								style={{
									width: `calc((100% - ${visibleCount - 1}rem) / ${visibleCount})`,
								}}
							>
								<div
									className="relative aspect-[4/3] w-full overflow-hidden"
									style={{ backgroundColor: imageBgColor }}
								>
									<Image
										src={item.src}
										alt={`${t(altKey)} ${index + 1}`}
										fill
										className="object-contain"
										sizes="(max-width: 768px) 50vw, 25vw"
									/>
								</div>
							</div>
						))}
					</div>
				</div>

				{slideCount > 1 && (
					<div className="mt-6 flex items-center justify-center gap-2 md:mt-8">
						{Array.from({ length: slideCount }, (_, i) => (
							<button
								key={i}
								onClick={() => setCurrentIndex(i)}
								className={`h-2.5 w-2.5 rounded-full transition-colors duration-300 ${
									currentIndex === i ? "bg-[#9acd32]" : "bg-[#c8e69c]"
								}`}
								aria-label={`Go to page ${i + 1}`}
							/>
						))}
					</div>
				)}
			</div>
		</section>
	);
}
